import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { DollarSign, Clock, MapPin, Settings as SettingsIcon, Info } from 'lucide-react';

const Settings = () => {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [commissionRate, setCommissionRate] = useState('');
  const [stopFee, setStopFee] = useState('');
  const [stopTime, setStopTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setCommissionRate(response.data.commission_percentage?.toString() || '20');
      setStopFee(response.data.stop_fee?.toString() || '15');
      setStopTime(response.data.stop_time_minutes?.toString() || '15');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        commission_percentage: parseFloat(commissionRate)
      };
      
      // Only admin can update stop pricing
      if (isAdmin) {
        updateData.stop_fee = parseFloat(stopFee);
        updateData.stop_time_minutes = parseInt(stopTime);
      }

      const response = await axios.put(`${API}/settings`, updateData);
      setSettings(response.data);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading settings...</div>
      </div>
    );
  }

  // Calculate preview with stops
  const sampleRideTotal = 100;
  const numSampleStops = 2;
  const stopFeeValue = parseFloat(stopFee) || 15;
  const stopTimeValue = parseInt(stopTime) || 15;
  const stopsTotal = numSampleStops * stopFeeValue;
  const stopsTimeCost = (numSampleStops * stopTimeValue) * 0.65; // per_minute rate

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <SettingsIcon className="h-7 w-7" />
          Settings
        </h2>
        <p className="text-gray-600 mt-1">Manage your ride tracker preferences</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Commission Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Commission Settings
            </CardTitle>
            <CardDescription>
              Adjust the commission percentage that is deducted from your earnings.
              Changes will apply to all new rides.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="commission">Commission Percentage (%)</Label>
              <Input
                id="commission"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                required
                className="form-input"
                data-testid="commission-input"
              />
              <p className="text-sm text-gray-500">
                Current rate: {settings?.commission_percentage}%
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Preview</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Ride Total:</span>
                  <span className="font-medium">$100.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-600">Commission ({commissionRate}%):</span>
                  <span className="font-medium text-orange-600">
                    -${(100 * (parseFloat(commissionRate) / 100)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-blue-200">
                  <span className="font-semibold">Your Net Earnings:</span>
                  <span className="font-bold text-green-600">
                    ${(100 - (100 * (parseFloat(commissionRate) / 100))).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stop Pricing Settings - Admin Only */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-purple-600" />
                Stop Pricing Settings
              </CardTitle>
              <CardDescription>
                Configure pricing for intermediate stops on rides.
                These settings apply to all price calculations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stopFee" className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    Fee Per Stop ($)
                  </Label>
                  <Input
                    id="stopFee"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={stopFee}
                    onChange={(e) => setStopFee(e.target.value)}
                    required
                    data-testid="stop-fee-input"
                  />
                  <p className="text-xs text-gray-500">
                    Flat fee charged for each additional stop
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stopTime" className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Minutes Per Stop
                  </Label>
                  <Input
                    id="stopTime"
                    type="number"
                    step="1"
                    min="0"
                    max="60"
                    value={stopTime}
                    onChange={(e) => setStopTime(e.target.value)}
                    required
                    data-testid="stop-time-input"
                  />
                  <p className="text-xs text-gray-500">
                    Extra time added per stop (affects time-based pricing)
                  </p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Stop Pricing Preview (2 stops)
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Stop Fee (2 × ${stopFeeValue}):</span>
                    <span className="font-medium">${stopsTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Stop Time Cost (2 × {stopTimeValue}min × $0.65):</span>
                    <span className="font-medium">${stopsTimeCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-purple-200">
                    <span className="font-semibold">Total Stop Charges:</span>
                    <span className="font-bold text-purple-600">
                      ${(stopsTotal + stopsTimeCost).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 text-sm">
                <p className="text-gold-800">
                  <strong>Note:</strong> These settings affect price calculations for all users 
                  when they add stops to their rides.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non-admin info about stop pricing */}
        {!isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-purple-600" />
                Stop Pricing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fee Per Stop:</span>
                  <span className="font-medium">${settings?.stop_fee || 15}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time Per Stop:</span>
                  <span className="font-medium">{settings?.stop_time_minutes || 15} minutes</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Contact admin to adjust stop pricing settings.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <Button
          type="submit"
          className="w-full"
          disabled={saving}
          data-testid="save-settings-button"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>

      {/* Application Info */}
      <Card>
        <CardHeader>
          <CardTitle>Application Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Week Start Day:</span>
            <span className="font-medium">Sunday</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Date Format:</span>
            <span className="font-medium">MM/DD/YYYY</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Export Format:</span>
            <span className="font-medium">CSV</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
